/**
 * theme.ts — the portal's light/dark preference, in one place.
 *
 * THREE STATES, NOT TWO. `system` is the default and honours the viewer's OS
 * setting, because a person who has told their machine they want dark has
 * already answered this question once and should not have to answer it again.
 * `light` and `dark` are explicit overrides that beat the OS in both
 * directions.
 *
 * HOW IT REACHES CSS
 * ──────────────────
 * The resolved value is stamped on `<html data-theme="...">`, and
 * portal/src/globals.css defines its dark tokens under `:root[data-theme='dark']`.
 * `system` stamps the RESOLVED value rather than nothing — the more usual
 * pattern is to stamp nothing and let a `prefers-color-scheme` block handle it,
 * but the portal deliberately has no such block: dark is opt-in while the
 * client app still carries theme-blind classes, and a media query would flip
 * every dark-OS customer into a half-built theme with no way back.
 * Stamping the resolved value also makes the live state inspectable in devtools,
 * which a media query does not.
 *
 * WHY IT LIVES IN @sighthost/ui
 * ────────────────────────────
 * So the marketing site can adopt the same store when it stops being
 * dark-only, rather than growing a second implementation that drifts. Nothing
 * here imports React — it is a plain store, usable from the pre-paint inline
 * script in index.html as well as from the toggle component.
 *
 * EVERY STORAGE ACCESS IS GUARDED. localStorage throws outright in some
 * contexts (Safari private mode, embedded webviews, blocked site data). A
 * theme preference is not worth a white screen, so every read and write falls
 * back to the default rather than propagating.
 */

/**
 * The storage key and the pre-paint script live in plain JS next door so that
 * portal/vite.config.ts (which runs in Node) can inject the script into
 * index.html from the SAME definition the app uses. See themePrepaint.js.
 */
export { THEME_STORAGE_KEY, THEME_PREPAINT_SCRIPT } from './themePrepaint';
import { THEME_STORAGE_KEY } from './themePrepaint';

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';


const PREFERENCES: readonly ThemePreference[] = ['light', 'dark', 'system'];

function isPreference(v: unknown): v is ThemePreference {
  return typeof v === 'string' && (PREFERENCES as readonly string[]).includes(v);
}

/** The stored preference, or `system` when absent, invalid, or unreadable. */
export function getThemePreference(): ThemePreference {
  try {
    const raw = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isPreference(raw) ? raw : 'system';
  } catch {
    return 'system';
  }
}

/** What the OS is asking for. Defaults to `light` when unknowable. */
export function getSystemTheme(): ResolvedTheme {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

export function resolveTheme(preference: ThemePreference): ResolvedTheme {
  return preference === 'system' ? getSystemTheme() : preference;
}

/**
 * Stamp the resolved theme on `<html>`.
 *
 * Also sets `color-scheme`, which is not cosmetic: it is what makes the
 * browser render native form controls, scrollbars and the `<select>` dropdown
 * chrome in the matching theme. Without it a dark page shows a light native
 * dropdown, which is the tell that a dark mode was bolted on.
 */
export function applyTheme(preference: ThemePreference): ResolvedTheme {
  const resolved = resolveTheme(preference);
  const root = document.documentElement;
  root.dataset.theme = resolved;
  root.style.colorScheme = resolved;
  return resolved;
}

/** Persist and apply. Storage failure must not stop the theme from changing. */
export function setThemePreference(preference: ThemePreference): ResolvedTheme {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    /* Private mode / blocked site data — apply for this session anyway. */
  }
  return applyTheme(preference);
}

/**
 * Re-apply when the OS flips, but ONLY while the preference is `system`.
 * Returns an unsubscribe function.
 *
 * An explicit choice must survive the OS changing under it — that is the
 * difference between "I prefer dark" and "match my machine", and collapsing
 * them is the commonest bug in theme toggles.
 */
export function subscribeToSystemTheme(onChange: (resolved: ResolvedTheme) => void): () => void {
  let mq: MediaQueryList;
  try {
    mq = window.matchMedia('(prefers-color-scheme: dark)');
  } catch {
    return () => {};
  }
  const handler = () => {
    if (getThemePreference() !== 'system') return;
    onChange(applyTheme('system'));
  };
  mq.addEventListener('change', handler);
  return () => mq.removeEventListener('change', handler);
}
